import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import {
  ArrowRight,
  ChartLineUp,
  Megaphone,
  Palette,
  Globe,
  InstagramLogo,
  MagnifyingGlass,
  CheckCircle,
  Phone,
  EnvelopeSimple,
  MapPin,
  List,
  X,
  Plus,
  Minus,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_a3678ac8-3365-49c9-8022-ded2f37872cd/artifacts/hydsn7gh_logo%20goatcam%20.jpg";
const CARLOS_URL =
  "https://customer-assets.emergentagent.com/job_a3678ac8-3365-49c9-8022-ded2f37872cd/artifacts/2tt55pha_carlos%20foto%20traje%201.jpeg";
const HERO_BG =
  "https://images.unsplash.com/photo-1557401937-674407811c92?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const OFFICE_IMG =
  "https://images.unsplash.com/photo-1758518726775-70e538b0d46e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const services = [
  {
    icon: ChartLineUp,
    title: "Business Consulting",
    desc: "Strategic guidance across operations, growth planning and go-to-market execution for ambitious teams.",
    points: ["Operational audits", "Growth strategy", "Market positioning", "KPI frameworks"],
  },
  {
    icon: Megaphone,
    title: "Marketing Strategy",
    desc: "Clear, data-driven marketing plans designed to turn attention into measurable pipeline and revenue.",
    points: ["Full-funnel strategy", "Customer research", "Campaign planning", "Attribution setup"],
  },
  {
    icon: Palette,
    title: "Brand Development",
    desc: "Identity systems, naming, messaging and visual language that make your company unmistakable.",
    points: ["Brand identity", "Messaging frameworks", "Visual systems", "Brand guidelines"],
  },
  {
    icon: Globe,
    title: "Digital Marketing",
    desc: "Performance campaigns across paid search, display and programmatic that scale efficiently.",
    points: ["Paid search & social", "Creative testing", "Landing page CRO", "Reporting dashboards"],
  },
  {
    icon: InstagramLogo,
    title: "Social Media Management",
    desc: "Content calendars, community management and creator partnerships that actually move the needle.",
    points: ["Content production", "Community growth", "Influencer outreach", "Analytics & reporting"],
  },
  {
    icon: MagnifyingGlass,
    title: "SEO & Content Marketing",
    desc: "Technical SEO, editorial strategy and high-intent content that compounds traffic month over month.",
    points: ["Technical audits", "Keyword strategy", "Editorial roadmaps", "Link development"],
  },
];

const whyUs = [
  { title: "Senior Expertise", desc: "Every engagement is led by senior consultants — no juniors learning on your dime." },
  { title: "Proven Results", desc: "Measurable outcomes: stronger pipelines, sharper positioning, better conversion." },
  { title: "Personalized Service", desc: "Custom strategies built around your market, goals and internal capacity." },
  { title: "Transparent Pricing", desc: "Flat-fee or retainer models. No surprise invoices, no vague deliverables." },
  { title: "Integrated Team", desc: "Strategy, creative and execution under one roof, aligned on the same outcomes." },
  { title: "Long-term Partnership", desc: "We stay through execution — quarterly reviews keep strategy accountable." },
];

const testimonials = [
  {
    quote:
      "Goatcam rebuilt our go-to-market motion from the ground up. Within two quarters, qualified demo bookings were up 3x and our sales cycle shortened by nearly a month.",
    name: "Sarah Mitchell",
    role: "COO, B2B SaaS Company",
  },
  {
    quote:
      "Their brand refresh and messaging work completely changed how the market perceives us. We now close deals we wouldn't have even been invited to a year ago.",
    name: "James Rodriguez",
    role: "Founder, Professional Services Firm",
  },
  {
    quote:
      "We hired Goatcam for a 90-day marketing audit and ended up keeping them on retainer. Clear thinking, crisp execution, zero fluff.",
    name: "Emily Chen",
    role: "VP Marketing, Consumer Brand",
  },
  {
    quote:
      "The content and SEO program Goatcam built is now our top source of inbound leads. It compounds every month and has cut our paid acquisition costs dramatically.",
    name: "Michael Thompson",
    role: "Marketing Director, eCommerce",
  },
];

const faqs = [
  {
    q: "What types of clients do you typically work with?",
    a: "We partner with growth-stage companies, established SMBs and professional services firms across B2B and consumer categories. Most engagements start between $5k–$25k/month.",
  },
  {
    q: "How does a typical engagement start?",
    a: "Every engagement begins with a complimentary discovery call, followed by a paid audit or strategy sprint (2–4 weeks). From there we scope a retainer or project engagement tailored to your goals.",
  },
  {
    q: "How quickly will we see results?",
    a: "Strategy deliverables are in-hand within weeks. Measurable marketing results (pipeline, traffic, conversion lift) typically appear within 60–120 days, depending on channel maturity.",
  },
  {
    q: "Do you offer project-based or retainer work?",
    a: "Both. Short sprints (brand refresh, audits, launch campaigns) are project-based. Ongoing marketing, content and consulting are retainers.",
  },
  {
    q: "What industries do you specialize in?",
    a: "B2B SaaS, professional services, consumer goods, and growth-focused SMBs. Our frameworks translate across industries because they're grounded in fundamentals.",
  },
  {
    q: "Where are you based?",
    a: "Our headquarters is in Dallas, Texas, and we work with clients across the United States remotely and onsite.",
  },
];

function Header({ onNavClick }) {
  const [open, setOpen] = useState(false);
  const links = [
    { id: "services", label: "Services" },
    { id: "why-us", label: "Why Us" },
    { id: "team", label: "Team" },
    { id: "testimonials", label: "Clients" },
    { id: "faq", label: "FAQ" },
    { id: "contact", label: "Contact" },
  ];
  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-black/5"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="logo-link">
          <img src={LOGO_URL} alt="Goatcam Enterprises" className="h-10 w-10 object-contain" />
          <div className="leading-tight">
            <div className="font-heading font-bold text-[15px] tracking-tight">Goatcam Enterprises</div>
            <div className="overline text-[10px]" style={{ letterSpacing: "0.15em" }}>
              Consulting &amp; Marketing
            </div>
          </div>
        </a>
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => onNavClick(l.id)}
              className="text-sm font-medium text-[#0A0A0A] hover:text-[#002FA7] transition-colors"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>
        <button
          onClick={() => onNavClick("contact")}
          className="btn-primary hidden md:inline-flex"
          data-testid="header-cta-button"
        >
          Book a Consultation <ArrowRight size={16} weight="bold" />
        </button>
        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <List size={24} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-black/5 bg-white" data-testid="mobile-nav">
          <div className="px-6 py-4 flex flex-col gap-4">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => {
                  onNavClick(l.id);
                  setOpen(false);
                }}
                className="text-left text-base font-medium"
                data-testid={`mobile-nav-link-${l.id}`}
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => {
                onNavClick("contact");
                setOpen(false);
              }}
              className="btn-primary justify-center"
              data-testid="mobile-header-cta"
            >
              Book a Consultation
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero({ onCta }) {
  return (
    <section id="top" className="relative overflow-hidden" data-testid="hero-section">
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-24 md:pt-32 md:pb-40">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
          <div className="overline fade-in-up" data-testid="hero-overline">
            Consulting &amp; Marketing Agency — Dallas, TX
          </div>
          <h1
            className="mt-6 text-5xl md:text-7xl font-black tracking-tighter text-[#0A0A0A] fade-in-up stagger-1"
            data-testid="hero-headline"
          >
            Architecting scalable growth for market leaders.
          </h1>
          <p
            className="mt-8 text-lg md:text-xl leading-relaxed text-[#52525B] max-w-2xl fade-in-up stagger-2"
            data-testid="hero-subheadline"
          >
            Goatcam Enterprises is a senior-led consulting and marketing agency. We build clear strategy,
            sharp brands, and measurable marketing programs for ambitious companies.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center fade-in-up stagger-3">
            <button onClick={onCta} className="btn-primary" data-testid="hero-primary-cta">
              Book a Consultation <ArrowRight size={16} weight="bold" />
            </button>
            <button
              onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-secondary"
              data-testid="hero-secondary-cta"
            >
              Explore Services
            </button>
          </div>
        </div>

        <div className="mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-4 gap-6 fade-in-up stagger-4 text-center">
          {[
            { k: "1,200+", v: "Clients Served" },
            { k: "7+", v: "Years in Business" },
            { k: "40+", v: "Industries" },
            { k: "98%", v: "Client Satisfaction" },
          ].map((s, i) => (
            <div key={i} className="border-t border-[#0A0A0A] pt-6" data-testid={`hero-stat-${i}`}>
              <div className="font-heading text-3xl md:text-4xl font-bold text-[#0A0A0A]">{s.k}</div>
              <div className="mt-2 text-sm text-[#52525B]">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" className="py-24 md:py-32 bg-white border-t border-[#E5E7EB]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <div className="overline">What we do</div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-[#0A0A0A]">
            Strategy, brand and marketing — integrated.
          </h2>
          <p className="mt-6 text-lg text-[#52525B]">
            Six practice areas, one team. We combine consulting rigor with creative execution so strategy doesn't
            stay in a deck — it shows up in your market.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="card text-center" data-testid={`service-card-${i}`}>
                <Icon size={36} weight="thin" className="text-[#002FA7] mx-auto" />
                <h3 className="mt-6 font-heading text-xl font-semibold text-[#0A0A0A]">{s.title}</h3>
                <p className="mt-3 text-[#52525B] leading-relaxed">{s.desc}</p>
                <ul className="mt-6 space-y-2 text-left inline-block">
                  {s.points.map((p, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-[#0A0A0A]">
                      <CheckCircle size={16} weight="fill" className="text-[#002FA7] mt-0.5 flex-shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  return (
    <section id="why-us" className="py-24 md:py-32 bg-[#FAFAFA]" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="overline">Why Goatcam</div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-[#0A0A0A]">
            Senior-led. Outcome-focused. Built for the long run.
          </h2>
          <p className="mt-6 text-lg text-[#52525B]">
            We don't sell templated retainers. Every engagement is scoped around a specific business outcome and
            led by people who've actually done the work.
          </p>
          <div className="mt-8 overflow-hidden border border-[#E5E7EB]">
            <img src={OFFICE_IMG} alt="Consulting team meeting" className="w-full h-64 object-cover" />
          </div>
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
          {whyUs.map((w, i) => (
            <div key={i} className="bg-white p-8" data-testid={`why-us-item-${i}`}>
              <div className="font-heading text-lg font-semibold text-[#0A0A0A]">{w.title}</div>
              <p className="mt-3 text-[#52525B] leading-relaxed text-sm">{w.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Team({ onCta }) {
  return (
    <section id="team" className="py-24 md:py-32 bg-white border-t border-[#E5E7EB]" data-testid="team-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-5">
          <div className="overline">Leadership</div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-[#0A0A0A]">
            Meet your lead consultant.
          </h2>
          <p className="mt-6 text-lg text-[#52525B]">
            With 7+ years building and advising consulting and marketing programs, Carlos leads every engagement
            personally — setting strategy, directing the team and reporting to clients directly.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-6">
            <div>
              <div className="font-heading text-3xl font-bold text-[#0A0A0A]">7+</div>
              <div className="mt-1 text-sm text-[#52525B]">Years Advising</div>
            </div>
            <div>
              <div className="font-heading text-3xl font-bold text-[#0A0A0A]">40+</div>
              <div className="mt-1 text-sm text-[#52525B]">Industries</div>
            </div>
          </div>
          <button onClick={onCta} className="btn-primary mt-10" data-testid="team-cta-button">
            Schedule a Consultation <ArrowRight size={16} weight="bold" />
          </button>
        </div>
        <div className="md:col-span-7">
          <div className="relative border border-[#E5E7EB] overflow-hidden">
            <img src={CARLOS_URL} alt="Carlos — Head Consultant" className="w-full h-[540px] object-cover" />
            <div className="absolute bottom-0 left-0 right-0 bg-white/90 backdrop-blur-sm p-6 border-t border-[#E5E7EB]">
              <div className="font-heading text-xl font-bold">Carlos</div>
              <div className="text-sm text-[#52525B] mt-1">Head Consultant, Consulting &amp; Marketing Strategy</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section id="testimonials" className="py-24 md:py-32 bg-[#0A0A0A] text-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#7A9FFF" }}>
            Client Outcomes
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-white">
            Work that clients put in their board decks.
          </h2>
        </div>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className="border border-white/15 p-8 hover:border-white/40 transition-colors"
              data-testid={`testimonial-${i}`}
            >
              <div className="text-lg leading-relaxed">&ldquo;{t.quote}&rdquo;</div>
              <div className="mt-6 pt-6 border-t border-white/15">
                <div className="font-heading font-semibold">{t.name}</div>
                <div className="text-sm text-white/60 mt-1">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 md:py-32 bg-white border-t border-[#E5E7EB]" data-testid="faq-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="overline">FAQ</div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-[#0A0A0A]">
            Questions, answered.
          </h2>
          <p className="mt-6 text-[#52525B]">
            Don't see your question? Reach out directly — we respond within one business day.
          </p>
        </div>
        <div className="md:col-span-8">
          <div className="border-t border-[#E5E7EB]">
            {faqs.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={i} className="border-b border-[#E5E7EB]" data-testid={`faq-item-${i}`}>
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between text-left py-6 gap-6"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-heading text-lg font-semibold text-[#0A0A0A]">{f.q}</span>
                    {isOpen ? <Minus size={20} weight="bold" /> : <Plus size={20} weight="bold" />}
                  </button>
                  {isOpen && (
                    <div className="pb-6 text-[#52525B] leading-relaxed" data-testid={`faq-answer-${i}`}>
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "Business Consulting",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({
        state: "success",
        msg: "Thanks — we'll be in touch within one business day.",
      });
      setForm({ name: "", email: "", phone: "", service: "Business Consulting", message: "" });
    } catch (err) {
      setStatus({
        state: "error",
        msg: "Something went wrong. Please try again or email us directly.",
      });
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#FAFAFA] border-t border-[#E5E7EB]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="overline">Get in touch</div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-[#0A0A0A]">
            Let's build your next growth chapter.
          </h2>
          <p className="mt-6 text-lg text-[#52525B]">
            Tell us about your business and what you want to accomplish. We'll follow up within one business day
            to schedule a complimentary consultation.
          </p>
          <div className="mt-10 space-y-6">
            <a
              href="tel:+19455458851"
              className="flex items-start gap-4 hover:text-[#002FA7] transition-colors"
              data-testid="contact-phone"
            >
              <Phone size={22} weight="thin" className="mt-1 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-widest text-[#52525B]">Call</div>
                <div className="font-heading text-lg font-semibold">(945) 545-8851</div>
              </div>
            </a>
            <a
              href="mailto:sales@goatcamenterprises.com"
              className="flex items-start gap-4 hover:text-[#002FA7] transition-colors"
              data-testid="contact-email"
            >
              <EnvelopeSimple size={22} weight="thin" className="mt-1 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-widest text-[#52525B]">Email</div>
                <div className="font-heading text-lg font-semibold">sales@goatcamenterprises.com</div>
              </div>
            </a>
            <div className="flex items-start gap-4" data-testid="contact-address">
              <MapPin size={22} weight="thin" className="mt-1 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-widest text-[#52525B]">Office</div>
                <div className="font-heading text-lg font-semibold">
                  1910 Pacific Ave Ste 2000
                  <br />
                  Dallas, Texas 75201
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            className="bg-white border border-[#E5E7EB] p-8 md:p-10"
            data-testid="contact-form"
          >
            <h3 className="font-heading text-2xl font-semibold">Start a conversation</h3>
            <p className="mt-2 text-sm text-[#52525B]">All fields marked * are required.</p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#52525B]">Full Name *</label>
                <input
                  required
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 w-full border border-[#E5E7EB] px-4 py-3 bg-white"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#52525B]">Email *</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 w-full border border-[#E5E7EB] px-4 py-3 bg-white"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#52525B]">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-2 w-full border border-[#E5E7EB] px-4 py-3 bg-white"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#52525B]">Service Interest *</label>
                <select
                  required
                  value={form.service}
                  onChange={(e) => setForm({ ...form, service: e.target.value })}
                  className="mt-2 w-full border border-[#E5E7EB] px-4 py-3 bg-white"
                  data-testid="contact-service-select"
                >
                  <option>Business Consulting</option>
                  <option>Marketing Strategy</option>
                  <option>Brand Development</option>
                  <option>Digital Marketing</option>
                  <option>Social Media Management</option>
                  <option>SEO &amp; Content Marketing</option>
                  <option>General Inquiry</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-[#52525B]">Message</label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-2 w-full border border-[#E5E7EB] px-4 py-3 bg-white"
                  data-testid="contact-message-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={status.state === "loading"}
              className="btn-primary mt-8 disabled:opacity-60"
              data-testid="contact-submit-button"
            >
              {status.state === "loading" ? "Sending..." : "Request a Consultation"}
              <ArrowRight size={16} weight="bold" />
            </button>

            {status.state === "success" && (
              <div className="mt-6 text-sm text-[#002FA7]" data-testid="contact-success">
                {status.msg}
              </div>
            )}
            {status.state === "error" && (
              <div className="mt-6 text-sm text-red-600" data-testid="contact-error">
                {status.msg}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0A0A0A] text-white py-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Goatcam Enterprises" className="h-10 w-10 object-contain bg-white rounded" />
            <div>
              <div className="font-heading font-bold">Goatcam Enterprises LLC</div>
              <div className="text-xs text-white/60 uppercase tracking-widest mt-1">
                Consulting &amp; Marketing
              </div>
            </div>
          </div>
          <p className="mt-6 text-sm text-white/60 max-w-sm">
            A senior-led consulting and marketing agency helping ambitious companies build brand, strategy and
            pipeline that compounds.
          </p>
        </div>
        <div className="md:col-span-3">
          <div className="text-xs uppercase tracking-widest text-white/60">Services</div>
          <ul className="mt-4 space-y-2 text-sm text-white/85">
            <li>Business Consulting</li>
            <li>Marketing Strategy</li>
            <li>Brand Development</li>
            <li>Digital Marketing</li>
            <li>Social Media Management</li>
            <li>SEO &amp; Content Marketing</li>
          </ul>
        </div>
        <div className="md:col-span-4">
          <div className="text-xs uppercase tracking-widest text-white/60">Contact</div>
          <ul className="mt-4 space-y-2 text-sm text-white/85">
            <li>(945) 545-8851</li>
            <li>sales@goatcamenterprises.com</li>
            <li>
              1910 Pacific Ave Ste 2000
              <br />
              Dallas, Texas 75201
            </li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="text-xs text-white/50">
          © {new Date().getFullYear()} Goatcam Enterprises LLC. All rights reserved.
        </div>
        <div className="text-xs text-white/50">Consulting &amp; Marketing Agency · Dallas, TX</div>
      </div>
    </footer>
  );
}

function App() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});
  }, []);

  return (
    <div className="App" data-testid="app-root">
      <Header onNavClick={scrollTo} />
      <main>
        <Hero onCta={() => scrollTo("contact")} />
        <Services />
        <WhyUs />
        <Team onCta={() => scrollTo("contact")} />
        <Testimonials />
        <FAQ />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
